import { AuthApiError } from "@onlooker/auth-core";
/**
 * When a session check fails, whether the session is gone or only
 * unreachable for now (ONL-243).
 *
 * Only a 401 says the server no longer recognizes the session. A 429, a 5xx,
 * a network failure or a timeout says nothing about the session at all, and
 * signing someone out over one threw away a refresh token that still worked.
 */
/** Waits between retries when the server gave no Retry-After. */
export const RETRY_BACKOFF_MS = [
    5_000, 10_000, 20_000, 40_000, 60_000,
];
export function isSessionGone(error) {
    return error instanceof AuthApiError && error.status === 401;
}
/**
 * The bounds on a server-sent Retry-After. A 0 (or a past date) is a tight
 * retry loop, and anything past about 24.8 days (2^31-1 ms) overflows
 * `setTimeout`, which then fires at once. An hour is far past anything the API
 * sends: the account limit sends 60 s and a Cloudflare 429 at most a day.
 */
export const MIN_RETRY_MS = 1_000;
export const MAX_RETRY_MS = 3_600_000;
/** The server's Retry-After when it sent one, otherwise the backoff. */
export function retryDelayMs(error, failures) {
    if (error instanceof AuthApiError && error.retryAfterMs !== undefined) {
        return Math.min(MAX_RETRY_MS, Math.max(MIN_RETRY_MS, error.retryAfterMs));
    }
    return RETRY_BACKOFF_MS[Math.min(failures, RETRY_BACKOFF_MS.length - 1)];
}
/**
 * Retry-After as milliseconds from `now`: delta-seconds or an HTTP date.
 * Undefined when the header is missing or unreadable, and never negative.
 */
export function parseRetryAfter(header, now) {
    if (header === null)
        return undefined;
    const value = header.trim();
    if (value === "")
        return undefined;
    const seconds = Number(value);
    if (Number.isFinite(seconds))
        return Math.max(0, seconds * 1000);
    const date = Date.parse(value);
    if (!Number.isNaN(date))
        return Math.max(0, date - now);
    return undefined;
}
